import { type ReactNode, type SelectHTMLAttributes, forwardRef } from 'react';
import { FormField } from './FormField';

/** A single option when using the `options` prop instead of `<option>` children. */
export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Label text, passed through to FormField. */
  label?: string;
  /** Renders the FormField required marker (`*`) and sets the native `required` attribute. */
  required?: boolean;
  /** Help text, passed through to FormField (wired into aria-describedby). */
  help?: string;
  /** Error text, passed through to FormField (sets aria-invalid, extends aria-describedby) and switches the border to red. */
  error?: string;
  /** Rendered as a disabled, unselectable first `<option value="">`. */
  placeholder?: string;
  /** Options to render. Alternative to passing `<option>` elements as children. */
  options?: SelectOption[];
  /**
   * Explicit control id, forwarded to FormField (#14950). Without it FormField derives the id
   * from the LABEL TEXT, so N selects sharing a label — a per-row picker in a list, say — all
   * render `id="internal-account"`. Duplicate ids break `htmlFor` association: every label then
   * points at the first control, so a screen reader (and `getByLabelText`) cannot tell the rows
   * apart. Pass a per-instance id in that case.
   */
  htmlFor?: string;
  children?: ReactNode;
}

/**
 * Select — a shared, styled `<select>` composing FormField for label /
 * required-marker / help / error chrome (#14003), matching Input's control
 * classes (h-9, rounded-md border, indigo focus ring, red error variant) so
 * the native select doesn't stand out from the rest of the form.
 *
 * Options are supplied either as `<option>` children or via the `options`
 * prop; a `placeholder` renders as a disabled first option.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { label, required, help, error, className = '', placeholder, options, htmlFor, children, ...rest },
    ref,
  ) => {
    return (
      <FormField label={label} htmlFor={htmlFor} required={required} help={help} error={error}>
        {(controlProps) => (
          <select
            ref={ref}
            required={required}
            className={`h-9 w-full rounded-md border px-3 text-sm transition-colors
              ${error ? 'border-red-500 focus:ring-red-500/50' : 'border-gray-300 focus:ring-indigo-500/50 focus:border-indigo-500'}
              focus:outline-none focus:ring-2
              ${className}`}
            {...rest}
            {...controlProps}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options
              ? options.map((option) => (
                  <option key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </option>
                ))
              : children}
          </select>
        )}
      </FormField>
    );
  },
);

Select.displayName = 'Select';
